import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';

type Keys = string[];
type KeyboardEvtCallback = () => void;

export default function useKeyPress(
  keys: Keys = [],
  callback: KeyboardEvtCallback,
) {
  const callbackRef = useRef(callback);
  const pressedKeysRef = useRef<Set<string>>(new Set());

  useLayoutEffect(() => {
    callbackRef.current = callback;
  });

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      pressedKeysRef.current.add(event.key);

      // Check if all keys in the combination are pressed
      if (keys.every((key) => pressedKeysRef.current.has(key))) {
        callbackRef.current();
      }
    },
    [keys],
  );

  const handleKeyUp = useCallback((event: KeyboardEvent) => {
    pressedKeysRef.current.delete(event.key);
  }, []);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('keyup', handleKeyUp);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('keyup', handleKeyUp);
    };
  }, [handleKeyDown, handleKeyUp]);
}
